import { Component, inject } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { AsyncPipe, DatePipe } from '@angular/common';
import { Company } from '../../models/company.model';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { SharedConfirmDialogComponent } from '@tangible/shared';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Toast } from 'primeng/toast';
import { DrawerModule } from 'primeng/drawer';
import { Tooltip } from 'primeng/tooltip';
import { parseApiError } from '../quotes-api-error';
import { normalizeWebsite } from '../normalize-website';
import { CustomersService } from '../services/customers.service';

@Component({
  selector: 'app-customers-page',
  imports: [
    ButtonModule,
    TableModule,
    DrawerModule,
    AsyncPipe,
    DatePipe,
    ReactiveFormsModule,
    InputTextModule,
    TextareaModule,
    SharedConfirmDialogComponent,
    Toast,
    Tooltip,
  ],
  providers: [ConfirmationService, MessageService],
  templateUrl: './customers-page.component.html',
  styleUrl: './customers-page.component.scss',
})
export class CustomersPageComponent {
  private customersService = inject(CustomersService);
  companies$ = this.customersService.companies$;

  confirmationService = inject(ConfirmationService);
  messageService = inject(MessageService);

  isDrawerVisible = false;
  companyInDrawer: Company | null = null;
  isSaving = false;

  constructor() {
    this.customersService.loadCompanies();
  }

  companyFormGroup = new FormGroup({
    name: new FormControl('', [Validators.required]),
    email: new FormControl(''),
    phone: new FormControl(''),
    address: new FormControl(''),
    website: new FormControl(''),
  });

  openDrawer(company: Company | null = null) {
    if (company) {
      this.companyInDrawer = company;
      this.companyFormGroup.patchValue({
        name: company.name,
        email: company.email,
        phone: company.phone,
        address: company.address,
        website: company.website,
      });
    } else {
      this.companyInDrawer = null;
      this.companyFormGroup.reset({
        name: '',
        email: '',
        phone: '',
        address: '',
        website: '',
      });
    }
    this.isDrawerVisible = true;
  }

  closeDrawer() {
    this.isDrawerVisible = false;
    this.companyFormGroup.reset();
  }

  private normalizeOptional(value: string | null | undefined): string {
    return (value ?? '').trim();
  }

  private getPayload() {
    const formValue = this.companyFormGroup.value;
    return {
      name: this.normalizeOptional(formValue.name),
      email: this.normalizeOptional(formValue.email),
      phone: this.normalizeOptional(formValue.phone),
      address: this.normalizeOptional(formValue.address),
      website: normalizeWebsite(formValue.website),
    };
  }

  async saveCompany() {
    if (this.companyFormGroup.invalid || this.isSaving) return;

    this.isSaving = true;
    const payload = this.getPayload();

    try {
      if (this.companyInDrawer) {
        await this.customersService.updateCompany(
          this.companyInDrawer.id,
          payload,
        );
        this.messageService.add({
          severity: 'success',
          summary: 'Company updated',
        });
      } else {
        await this.customersService.createCompany(payload);
        this.messageService.add({
          severity: 'success',
          summary: 'Company created',
        });
      }
      this.closeDrawer();
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Failed to save customer',
        detail: parseApiError(error, 'Failed to save customer.'),
      });
    } finally {
      this.isSaving = false;
    }
  }

  confirmDelete(company: Company) {
    this.confirmationService.confirm({
      message:
        `Are you sure you want to delete "${company.name}"? ` +
        'If this company is linked to existing quotes, deleting it will remove the company link from those quotes.',
      header: 'Delete Customer',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      acceptButtonStyleClass: 'p-button-danger',
      accept: async () => {
        try {
          await this.customersService.deleteCompany(company.id);
          this.messageService.add({
            severity: 'success',
            summary: 'Customer deleted',
          });
        } catch (error) {
          this.messageService.add({
            severity: 'error',
            summary: 'Failed to delete customer',
            detail: parseApiError(error, 'Failed to delete customer.'),
          });
        }
      },
    });
  }
}
